<script lang="ts">
  import type { RenderDto } from '@wood-viewer/shared';
  import { t } from '../i18n/index.svelte';
  import Icon from './Icon.svelte';

  let {
    renders,
    canDelete = false,
    standalone = false,
    onClose,
    onDelete,
    onSetThumbnail,
  }: {
    renders: RenderDto[];
    /** author/owner thấy nút Xóa từng ảnh. */
    canDelete?: boolean;
    /** Trang gallery công khai độc lập (không phải modal trên Viewer): ẩn nút đóng + nền đặc, không đóng khi chạm nền lưới. */
    standalone?: boolean;
    onClose?: () => void;
    onDelete?: (id: number) => void;
    /** author: đặt 1 ảnh render làm ảnh đại diện (thumbnail) của model. */
    onSetThumbnail?: (r: RenderDto) => void;
  } = $props();

  // author có thể đặt thumbnail (không hiện ở trang công khai vì canDelete=false ở đó)
  const canSetThumb = $derived(canDelete && !!onSetThumbnail);

  // chỉ ảnh có URL mới hiển thị/điều hướng được
  const shown = $derived(renders.filter((r) => r.imageUrl));
  // chỉ số ảnh đang phóng to trong `shown` (null = chưa mở lightbox)
  let zoomIndex = $state<number | null>(null);
  const current = $derived(zoomIndex !== null ? (shown[zoomIndex] ?? null) : null);

  function openAt(i: number) {
    zoomIndex = i;
  }
  function closeLightbox() {
    zoomIndex = null;
  }
  function prev() {
    if (zoomIndex === null || shown.length === 0) return;
    zoomIndex = (zoomIndex - 1 + shown.length) % shown.length; // cuộn vòng
  }
  function next() {
    if (zoomIndex === null || shown.length === 0) return;
    zoomIndex = (zoomIndex + 1) % shown.length;
  }

  function onGridBackdrop(e: MouseEvent) {
    if (standalone) return; // trang độc lập: không có gì để đóng về
    if (e.target === e.currentTarget) onClose?.();
  }
  function onLightboxBackdrop(e: MouseEvent) {
    // chỉ đóng khi chạm nền trống (không phải ảnh/nút)
    if (e.target === e.currentTarget) closeLightbox();
  }

  // ----- phím mũi tên / Esc (desktop) -----
  function onKey(e: KeyboardEvent) {
    if (zoomIndex === null) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeLightbox();
    }
  }

  // ----- vuốt trái/phải (mobile) -----
  let touchX = 0;
  let touchY = 0;
  function onTouchStart(e: TouchEvent) {
    const tch = e.changedTouches[0];
    touchX = tch.clientX;
    touchY = tch.clientY;
  }
  function onTouchEnd(e: TouchEvent) {
    const tch = e.changedTouches[0];
    const dx = tch.clientX - touchX;
    const dy = tch.clientY - touchY;
    // vuốt ngang đủ dài + trội hơn dọc (tránh nhầm với cuộn dọc)
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next();
      else prev();
    }
  }
</script>

<svelte:window onkeydown={onKey} />

<div class="rg-backdrop" class:standalone role="presentation" onclick={onGridBackdrop}>
  <div class="rg-panel" role="dialog" aria-modal="true" aria-label={t('airender.galleryTitle')}>
    <header class="rg-head">
      <h2><Icon name="image" size={18} /> {t('airender.galleryTitle')}</h2>
      {#if !standalone}
        <button class="x" aria-label={t('common.close')} onclick={() => onClose?.()}>
          <Icon name="close" size={18} />
        </button>
      {/if}
    </header>

    {#if shown.length === 0}
      <p class="rg-empty">{t('airender.galleryEmpty')}</p>
    {:else}
      <div class="rg-grid">
        {#each shown as r, i (r.id)}
          <div class="rg-cell">
            <button class="rg-thumb" onclick={() => openAt(i)} aria-label={t('airender.galleryTitle')}>
              <img src={r.imageUrl} alt={t('airender.galleryTitle')} loading="lazy" />
            </button>
            {#if canSetThumb}
              <button
                class="rg-setthumb"
                aria-label={t('airender.setThumbnail')}
                title={t('airender.setThumbnail')}
                onclick={() => onSetThumbnail?.(r)}
              >
                <Icon name="image" size={14} />
              </button>
            {/if}
            {#if canDelete && onDelete}
              <button class="rg-del" aria-label={t('airender.delete')} title={t('airender.delete')} onclick={() => onDelete(r.id)}>
                <Icon name="trash" size={14} />
              </button>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

{#if current?.imageUrl}
  <div
    class="rg-lightbox"
    role="presentation"
    onclick={onLightboxBackdrop}
    ontouchstart={onTouchStart}
    ontouchend={onTouchEnd}
  >
    <button class="lb-close" aria-label={t('common.close')} onclick={closeLightbox}>
      <Icon name="close" size={20} />
    </button>

    {#if canSetThumb && current}
      <button class="lb-action" onclick={() => onSetThumbnail?.(current)}>
        <Icon name="image" size={18} /><span>{t('airender.setThumbnail')}</span>
      </button>
    {/if}

    {#if shown.length > 1}
      <button class="lb-nav prev" aria-label={t('airender.prevImage')} onclick={prev}>
        <Icon name="arrowLeft" size={24} />
      </button>
      <button class="lb-nav next" aria-label={t('airender.nextImage')} onclick={next}>
        <Icon name="arrowRight" size={24} />
      </button>
    {/if}

    <img src={current.imageUrl} alt={t('airender.galleryTitle')} />

    {#if shown.length > 1}
      <div class="lb-count">{(zoomIndex ?? 0) + 1} / {shown.length}</div>
    {/if}
  </div>
{/if}

<style>
  .rg-backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    display: grid;
    place-items: center;
    padding: var(--space-md);
    background: rgba(11, 31, 58, 0.42);
    backdrop-filter: blur(2px);
  }
  /* Trang gallery công khai độc lập: nền trang đặc (không phải overlay), panel lớn hơn. */
  .rg-backdrop.standalone {
    background: linear-gradient(var(--color-canvas-top), var(--color-canvas-bottom));
    backdrop-filter: none;
  }
  .rg-backdrop.standalone .rg-panel {
    width: min(1000px, 100%);
    max-height: 100%;
    height: 100%;
  }
  .rg-panel {
    display: flex;
    flex-direction: column;
    width: min(720px, 100%);
    max-height: 90vh;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    overflow: hidden;
  }
  .rg-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-md);
    border-bottom: 1px solid var(--color-border);
  }
  .rg-head h2 {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: var(--font-size-body);
    font-weight: 700;
    color: var(--color-navy);
  }
  .rg-head .x {
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-control);
  }
  .rg-head .x:hover {
    background: var(--color-canvas-bottom);
    color: var(--color-primary);
  }
  .rg-empty {
    margin: 0;
    padding: var(--space-xl);
    text-align: center;
    color: var(--color-text-secondary);
    font-size: var(--font-size-caption);
  }
  .rg-grid {
    overflow-y: auto;
    padding: var(--space-md);
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: var(--space-sm);
  }
  .rg-cell {
    position: relative;
  }
  .rg-thumb {
    display: block;
    width: 100%;
    padding: 0;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-canvas-bottom);
    cursor: pointer;
    overflow: hidden;
  }
  .rg-thumb img {
    display: block;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
  }
  .rg-thumb:hover {
    border-color: var(--color-primary);
  }
  .rg-del {
    position: absolute;
    top: 6px;
    right: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: var(--radius-control);
    background: rgba(11, 31, 58, 0.6);
    color: #fff;
    cursor: pointer;
  }
  .rg-del:hover {
    background: var(--color-error);
  }
  /* nút "đặt làm thumbnail" trên mỗi ô (author) — góc trái trên, đối diện nút xóa */
  .rg-setthumb {
    position: absolute;
    top: 6px;
    left: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: var(--radius-control);
    background: rgba(11, 31, 58, 0.6);
    color: #fff;
    cursor: pointer;
  }
  .rg-setthumb:hover {
    background: var(--color-primary);
  }

  /* ----- lightbox ----- */
  .rg-lightbox {
    position: fixed;
    inset: 0;
    z-index: calc(var(--z-modal) + 1);
    display: grid;
    place-items: center;
    padding: var(--space-lg);
    background: rgba(11, 31, 58, 0.82);
    touch-action: pan-y; /* cho vuốt ngang được bắt; vẫn cho cuộn dọc */
  }
  .rg-lightbox img {
    max-width: 92vw;
    max-height: 90vh;
    border-radius: var(--radius-control);
    box-shadow: var(--shadow-panel);
    user-select: none;
    -webkit-user-drag: none;
  }
  .lb-close {
    position: absolute;
    top: var(--space-md);
    right: var(--space-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    cursor: pointer;
  }
  .lb-close:hover {
    background: rgba(255, 255, 255, 0.28);
  }
  /* nút "đặt làm thumbnail" trong lightbox (author) — góc trái trên, có nhãn */
  .lb-action {
    position: absolute;
    top: var(--space-md);
    left: var(--space-md);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 40px;
    padding: 0 14px;
    border: none;
    border-radius: var(--radius-pill);
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
  }
  .lb-action:hover {
    background: var(--color-primary);
  }
  .lb-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    cursor: pointer;
  }
  .lb-nav:hover {
    background: rgba(255, 255, 255, 0.28);
  }
  .lb-nav.prev {
    left: var(--space-md);
  }
  .lb-nav.next {
    right: var(--space-md);
  }
  .lb-count {
    position: absolute;
    bottom: var(--space-lg);
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 12px;
    border-radius: var(--radius-pill);
    background: rgba(11, 31, 58, 0.6);
    color: #fff;
    font-size: var(--font-size-caption);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  /* Mobile: ẩn nút mũi tên, dùng vuốt; nút đóng to hơn cho dễ chạm */
  @media (max-width: 767px) {
    .lb-nav {
      display: none;
    }
  }
</style>
